import { AppState, Dispatch, ExtraArgs, GetState } from '../../../../types'
import { Alert, AlertResourceType, BasicAlert } from '../../model'
import { changeAlerts, changeFilteredAlerts, handleRedirectAlertLink, isLoading } from '../../AlertsListPage/actions'
import { getComponentsResourceTypeFromResource, getResourceId, isManagedAPI, Resource } from '../../../../common/resource/model'
import { storeEnvironmentsAndSelectOne } from '../../../../common/environment/actions'
import { fetchLoadingResources, storeResourcesAndSelectOne } from '../../../../common/resource/actions'
import { selectVersion, storeVersionsAndSelectOne, versionActionCreators } from '../../../../common/version/actions'
import {
  fetchAllResourcesAndVersions,
  fetchMainSelectorsOptionsAndSelect,
  fetchVersionsAndSelect,
  selectEnvironmentAndFetchAllResources,
  selectResourceAndFetchVersions
} from '../../../../common/selector/actions'
import { alertsOrigin, getBasicAlertsResourceType } from './model'
import { ResourcesFilter, SelectorOptions } from '../../../../common/selector/model'
import { basicAlertModalSelectors } from './selectors'
import { ARMServer, User } from '../../../../remote-api/models'
import { addToast } from '../../../Toasts/actions'
import { Toast } from '../../../Toasts/model'
import { fetchBasicAlertsLimitExceeded } from '../../actions'
import * as React from 'react'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { fetchAPIPolicies } from '../../../../common/policies/actions'
import { getSeverityLabel } from '../../AlertsListPage/model'
import UIApiRemoteApi from '../../../../remote-api/UIRemoteApi'
import { ResourceNotFoundError } from '../../../../common/error/ResourceNotFoundError'
import { getAlerts, getFilteredAlerts } from '../../AlertsListPage/selectors'

/**
 *  Action Constants
 */
export const HandleOpenModal = 'Monitoring/Alerts/BasicAlertModal/HandleOpen'
export const HandleCloseModal = 'Monitoring/Alerts/BasicAlertModal/HandleClose'
export const CleanResourcesSelectors = 'Monitoring/Alerts/BasicAlertModal/CleanResourcesSelectors'
export const ChangeExistingAlert = 'Monitoring/Alerts/BasicAlertModal/ChangeExistingAlert'
export const FetchServers = 'Monitoring/Alerts/BasicAlertModal/FetchServers'
export const ChangeServers = 'Monitoring/Alerts/BasicAlertModal/ChangeServers'
export const ChangeUsers = 'Monitoring/Alerts/BasicAlertModal/ChangeUsers'
export const CreatedBasicAlert = 'Monitoring/Alerts/BasicAlertModal/CreatedBasicAlert'
export const CreatingBasicAlertError = 'Monitoring/Alerts/BasicAlertModal/CreatingBasicAlertError'
export const ChangeAPIResponseCodesLimit = 'Monitoring/Alerts/BasicAlertModal/ChangeAPIResponseCodesLimit'

/**
 *  Action Type
 */
export type HandleOpenModalAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/HandleOpen'
}

export type HandleCloseModalAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/HandleClose'
}

export type CleanResourcesSelectorsAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/CleanResourcesSelectors'
}

export type ChangeExistingAlertAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/ChangeExistingAlert',
  payload: { basicAlert?: BasicAlert }
}

export type FetchServersAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/FetchServers'
}

export type ChangeServersAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/ChangeServers'
  payload: {
    servers: ARMServer[]
  }
}

export type ChangeUsersAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/ChangeUsers'
  payload: {
    users: User[]
  }
}

export type CreatedBasicAlertAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/CreatedBasicAlert'
  payload: {
    organizationId: string,
    environmentId: string,
    resourceType: string,
    metric: string,
    severity: string,
  }
}

export type CreatingBasicAlertErrorAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/CreatingBasicAlertError'
  payload: {
    category: string
  }
}

export type ChangeAPIResponseCodesLimitAction = {
  type: 'Monitoring/Alerts/BasicAlertModal/ChangeAPIResponseCodesLimit'
  payload: {
    apiResponseCodesLimit: number
  }
}

/**
 *  Action Creators
 */
export const handleOpenModal = (): HandleOpenModalAction => ({
  type: HandleOpenModal
})

export const handleCloseModal = (): HandleCloseModalAction => ({
  type: HandleCloseModal
})

export const cleanResourcesSelectors = (): CleanResourcesSelectorsAction => ({
  type: CleanResourcesSelectors
})

export const changeExistingAlertAction = (basicAlert?: BasicAlert): ChangeExistingAlertAction => ({
  type: ChangeExistingAlert,
  payload: {basicAlert}
})

export const initializeFetchServersAction = (): FetchServersAction => ({
  type: FetchServers
})

export const changeServersAction = (servers: ARMServer[]): ChangeServersAction => ({
  type: ChangeServers,
  payload: {servers}
})

export const changeUsersAction = (users: User[]): ChangeUsersAction => ({
  type: ChangeUsers,
  payload: {users}
})

export const createdBasicAlertAction = (basicAlert: BasicAlert): CreatedBasicAlertAction => ({
  type: CreatedBasicAlert,
  payload: {
    organizationId: basicAlert.organizationId || basicAlert.resource.organizationId,
    environmentId: basicAlert.resource.environmentId,
    resourceType: basicAlert.resource.type,
    metric: basicAlert.metricType,
    severity: getSeverityLabel(basicAlert.severity),
  }
})

export const creatingBasicAlertErrorAction = (category: string): CreatingBasicAlertErrorAction => ({
  type: CreatingBasicAlertError,
  payload: {category}
})

export const changeAPIResponseCodesLimitAction = (apiResponseCodesLimit: number): ChangeAPIResponseCodesLimitAction => ({
  type: ChangeAPIResponseCodesLimit,
  payload: {apiResponseCodesLimit}
})

export const handleOpen = () => (dispatch: Dispatch) => {
  dispatch(handleOpenModal())
}

export const handleClose = () => (dispatch: Dispatch) => {
  dispatch(handleCloseModal())
  dispatch(changeExistingAlertAction(undefined))
  window.location.hash = '/alerts'
}

export const changeExistingAlertByAlertId = (alertId: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  return getExistingBasicAlert(alertId, getState(), api.ui(getState))
    .then((existingBasicAlert) => {
      dispatch(changeExistingAlertAction(existingBasicAlert))
    })
}

/**
 * check the alert in the local redux first. if the alert cannot be found, make an api call to server to get the alert.
 * @param alertId
 * @param state
 * @param uiApiRemoteApi
 */
function getExistingBasicAlert(alertId: string, state: AppState, uiApiRemoteApi: UIApiRemoteApi): Promise<BasicAlert> {
  return new Promise((resolve, reject) => {
    // find the basic alert in the local redux store
    const filteredAlerts = getFilteredAlerts(state)
    const alerts = getAlerts(state)
    const basicAlertFromLocal = [...filteredAlerts, ...alerts].find(basicAlert => {
      const basicAlertId = (basicAlert as BasicAlert).alertId
      return basicAlertId ? basicAlertId.toString() === alertId : false
    })
    if (basicAlertFromLocal) {
      resolve(basicAlertFromLocal as BasicAlert)
      return
    }

    // make an api call to server to get the alert
    const organizationId = state.monitoring.auth.profile.organizationId
    uiApiRemoteApi.fetchAlert(organizationId, alertId)
      .then((response: {dataPromise: Promise<Alert>}) => {
        return response.dataPromise
      })
      .then((basicAlertFromServer) => {
        resolve(basicAlertFromServer as BasicAlert)
      })
      .catch((err) => {
        if (err.errorCode === 404) {
          reject(new ResourceNotFoundError(`Alert not found. alertId:${alertId}`))
        } else {
          reject(err)
        }
      })
  })
}

export const changeAPIResponseCodesLimit = (apiResponseCodesLimit: number) => (dispatch: Dispatch) => {
  dispatch(changeAPIResponseCodesLimitAction(apiResponseCodesLimit))
}

export const renderSuccessMessage = (basicAlert: BasicAlert) => {
  if (basicAlert.alertId) {
    return (
      <div className={'saved-basic-alert-toast-container'}>
        <span className={'saved-basic-alert-toast-text'}>"{basicAlert.alertName}" was successfully saved.</span>
        <a className={'saved-basic-alert-toast-link'} onClick={handleRedirectAlertLink(basicAlert.alertId)}>View</a>
      </div>
    )
  }
  return undefined
}

export const saveBasicAlert = (alert: BasicAlert) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(isLoading())
  return api.ui(getState).saveBasicAlert(alert.resource.organizationId, alert).then((newAlert: BasicAlert) => {
    if (!alert.alertId) {
      dispatch(createdBasicAlertAction(newAlert))
    }

    dispatch(fetchBasicAlertsLimitExceeded())

    const toast: Toast = {
      children: renderSuccessMessage(newAlert),
      kind: 'success',
      testId: 'saved-basic-alert-toast'
    }
    dispatch(addToast(toast))

    const oldAlerts = getState().monitoring.alert.alerts
    const filtered = getState().monitoring.alert.filtered

    if (alert.alertId) {
      const newAlerts = oldAlerts.map(oldAlert => [newAlert].find(alert => alert.alertId === oldAlert.alertId) || oldAlert)
      dispatch(changeAlerts(newAlerts))

      const resolvedFilteredAlerts = filtered.map(filteredAlert => [newAlert].find(alert => alert.alertId === filteredAlert.alertId) || filteredAlert)
      const newFilteredAlerts = filtered.length > 0 ? resolvedFilteredAlerts : []
      dispatch(changeFilteredAlerts(newFilteredAlerts))
    } else {
      const filteredOldAlerts = oldAlerts.filter(oldAlert => oldAlert.alertId !== newAlert.alertId)
      const newAlertArray: Alert[] = [{...newAlert, isNew: true}]
      dispatch(changeAlerts(newAlertArray.concat(filteredOldAlerts)))

      const filteredWithoutNew = filtered.filter(filteredAlert => filteredAlert.alertId !== newAlert.alertId)
      const newFilteredAlerts = filtered.length > 0 ? newAlertArray.concat(filteredWithoutNew) : []
      dispatch(changeFilteredAlerts(newFilteredAlerts))
    }
  }).catch(error => {
    const errorMsg = getSaveBasicAlertErrorCategory(error.errorCode, alert.resource.type)

    dispatch(creatingBasicAlertErrorAction(errorMsg.error))

    dispatch(fetchBasicAlertsLimitExceeded())

    const errorToast: Toast = {
      title: errorMsg.message,
      kind: 'error',
      testId: 'saved-basic-alert-toast-error'
    }
    dispatch(addToast(errorToast))

    const oldAlerts = getState().monitoring.alert.alerts
    dispatch(changeAlerts(oldAlerts))
    const filteredOldAlerts = getState().monitoring.alert.alerts
    dispatch(changeFilteredAlerts(filteredOldAlerts))
  })
}

export const deleteBasicAlert = (alert: BasicAlert) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(isLoading())
  return api.ui(getState).deleteBasicAlert(alert.resource.organizationId, alert).then(() => {
    const toast: Toast = {
      title: `"${alert.alertName}" was successfully deleted.`,
      kind: 'success',
      testId: 'deleted-basic-alert-toast'
    }
    dispatch(addToast(toast))

    dispatch(fetchBasicAlertsLimitExceeded())

    const oldAlerts = getState().monitoring.alert.alerts
    const newAlerts = oldAlerts.filter(oldAlert => oldAlert.alertId !== alert.alertId)
    dispatch(changeAlerts(newAlerts))

    const filteredOldAlerts = getState().monitoring.alert.filtered
    const newFilteredAlerts = filteredOldAlerts.filter(filteredOldAlert => filteredOldAlert.alertId !== alert.alertId)
    dispatch(changeFilteredAlerts(newFilteredAlerts))
  }).catch(() => {
    const errorToast: Toast = {
      title: 'There was a problem when deleting your alert. Try again later.',
      kind: 'error',
      testId: 'deleted-basic-alert-toast-error'
    }
    dispatch(addToast(errorToast))

    dispatch(fetchBasicAlertsLimitExceeded())

    const oldAlerts = getState().monitoring.alert.alerts
    dispatch(changeAlerts(oldAlerts))
    const filteredOldAlerts = getState().monitoring.alert.alerts
    dispatch(changeFilteredAlerts(filteredOldAlerts))
  })
}

export const load = (envId?: string, resourceId?: string, versionId?: string, targetId?: string, resType?: ResourceType) =>
  (dispatch: Dispatch, getState: GetState) => {
    const {environmentSelector, versionSelector} = getState().monitoring.basicAlertModal
    const resourceType = resourceId ? getBasicAlertsResourceType(resourceId, versionId, targetId) : resType
    const resourceSelector = basicAlertModalSelectors.resourceSelector.getAll(getState())
    const environment = environmentSelector.resources && environmentSelector.resources.find(env => env.id === envId)
    const resource = resourceSelector.resources && resourceSelector.resources.find((resource: Resource) =>
      getResourceId(resource) === resourceId && getComponentsResourceTypeFromResource(resource) === resourceType
    )
    const version = versionId && versionSelector.resources && versionSelector.resources.find(version => version.id === versionId)
    const options = alertsOptions(dispatch, {origin: alertsOrigin, targetId: targetId, resourceType})

    // check if selected environment is in store
    if (envId && environment && environmentSelector.resources) {
      dispatch(storeEnvironmentsAndSelectOne(environment, environmentSelector.resources, alertsOrigin))

      // check if selected resource is in store
      if (resource && resourceSelector.resources) {
        dispatch(fetchLoadingResources(options, envId, resourceSelector))
        dispatch(storeResourcesAndSelectOne(resource, resourceSelector.resources, alertsOrigin))

        // check if selected version is in store
        if (version && versionSelector.resources) {
          dispatch(storeVersionsAndSelectOne(version, versionSelector.resources, alertsOrigin))
        } else {
          dispatch(fetchVersionsAndSelect(options, resource, versionId))
        }
      } else { // the selected resource is not in the store, fetch all resources
        dispatch(selectVersion(options, [], undefined))
        dispatch(fetchAllResourcesAndVersions({...options, shouldSelect: true}, environment, resourceId, versionId))
      }
    } else { // the selected environment is not in the store, fetch all environments
      dispatch(selectVersion(options, [], undefined))
      dispatch(initializeFetchServers())
      dispatch(fetchMainSelectorsOptionsAndSelect(options, envId, resourceId, versionId)).then(() => {
        const env = getState().monitoring.basicAlertModal.environmentSelector.selected
        if (env) {
          dispatch(fetchServers(env.id))
        } else {
          dispatch(changeServersAction([]))
        }
      })
    }
  }

export const filterManagedAPIs = () => (): ResourcesFilter => {
  return (resources: Resource[], envId: string) => {
    return Promise.resolve(resources.filter(resource => !isManagedAPI(resource)))
  }
}

export const alertsOptions = (dispatch: Dispatch, opts: SelectorOptions) =>
  ({...opts, withMetrics: true, httpMetricsCompatible: true, filters: {resources: dispatch(filterManagedAPIs())}})

export const environmentSelected = (environmentId: string) => (dispatch: Dispatch, getState: GetState) => {
  const environmentSelector = basicAlertModalSelectors.environmentSelector.getAll(getState())
  const environments = environmentSelector.resources
  dispatch(versionActionCreators(alertsOrigin).fetchSuccess(undefined))
  dispatch(selectEnvironmentAndFetchAllResources(alertsOptions(dispatch, {origin: alertsOrigin}), environmentId, environments))
  dispatch(fetchServers(environmentId))
}

export const resourceSelected = (resourceId: string, resourceType: ResourceType) => (dispatch: Dispatch, getState: GetState) => {
  const resourceSelector = basicAlertModalSelectors.resourceSelector.getAll(getState())
  const resources = resourceSelector.resources
  const opts = alertsOptions(dispatch, {origin: alertsOrigin, resourceType})

  if (resourceType !== ResourceType.API && resourceType !== ResourceType.MANAGED_API) {
    dispatch(selectVersion(opts, [], undefined))
  }

  dispatch(selectResourceAndFetchVersions(opts, resourceId, resources))
}

export const versionSelected = (versionId: string) => (dispatch: Dispatch, getState: GetState) => {
  const versionSelector = basicAlertModalSelectors.versionSelector.getAll(getState())
  const versions = versionSelector.resources || []
  dispatch(selectVersion(alertsOptions(dispatch, {origin: alertsOrigin}), versions, versionId))
}

export const initializeFetchServers = () => (dispatch: Dispatch) => {
  dispatch(initializeFetchServersAction())
}

export const fetchServers = (envId: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(initializeFetchServers())
  const orgId = getState().monitoring.auth.profile.organizationId
  return api.ui(getState).fetchAMServers(orgId, envId).then((servers: ARMServer[]) => {
    dispatch(changeServersAction(servers))
  }).catch(() => dispatch(changeServersAction([])))
}

export const fetchPolicies = (orgId: string, apiId: string) => (dispatch: Dispatch) => {
  return dispatch(fetchAPIPolicies(orgId, apiId, alertsOrigin))
}

export const fetchUsers = (search: string, limit: number, offset: number) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const orgId = getState().monitoring.auth.profile.organizationId
  const queryParams = {search, limit, offset}
  return api.cs(getState).fetchUsers(orgId, queryParams)
}

export const changeUsers = (users: User[]) => (dispatch: Dispatch, getState: GetState) => {
  const propsUsers = getState().monitoring.basicAlertModal.users

  const resolvedUsers = propsUsers.concat(users.filter((user: User) => propsUsers.map(propUser => propUser.email).indexOf(user.email) < 0))
  dispatch(changeUsersAction(resolvedUsers))
}

export const getSaveBasicAlertErrorCategory = (errorCode: number, alertResourceType?: AlertResourceType): {error: string, message: string} => {
  switch (errorCode) {
    case 409:
      return {
        error: 'duplicated',
        message: 'The alert you are trying to create already exists. All alerts must be unique. Please create a different alert.'
      }
    case 403:
      return {
        error: 'limit-exceeded',
        // tslint:disable-next-line:max-line-length
        message: `You have reached the limit of active ${alertResourceType === AlertResourceType.API ? 'API' : 'Application'} alerts. Please try pausing an alert and retry.`
      }

    default:
      return {
        error: 'general',
        message: 'There was a problem when saving your alert. Try again later.'
      }
  }
}

export type BasicAlertModalActions =
  | HandleOpenModalAction
  | HandleCloseModalAction
  | CleanResourcesSelectorsAction
  | ChangeExistingAlertAction
  | FetchServersAction
  | ChangeServersAction
  | ChangeUsersAction
  | ChangeAPIResponseCodesLimitAction
